import React, { useEffect, useRef } from 'react'

import { bool, func, arrayOf, number, string } from 'prop-types'

import { useIs3dPrintModel } from 'Pods/ItemPage/TopSection/hooks/useTopSectionProps'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import { useCaseShape, printerTypeOptionShape } from '../../Utils/propTypes'
import UseCaseMediaViewer from '../UseCaseMediaViewer/UseCaseMediaViewer'

function SidebarGalleryExtension({
  isOpen,
  onClose: handleClose,
  onCloseSidebar: handleCloseSidebar,
  useCase = null,
  initialMediaIndex = 0,
  canDeleteUseCase,
  canEditUseCase,
  canUseLikeFeature,
  isFetchingPrinterTypes = false,
  onApprove,
  onDelete,
  onEditUseCase,
  onLike,
  onReject,
  onUnlike,
  printerTypeOptions = EMPTY_ARRAY,
  userIsAllowedToModerate,
  currentUsername = null,
  onShowNotification,
  isUpdating = false,
}) {
  const is3dPrintModel = useIs3dPrintModel()
  const panelRef = useRef(null)

  const canDelete = useCase ? canDeleteUseCase(useCase) : false
  const canEdit = (useCase && is3dPrintModel) ? canEditUseCase(useCase.username) : false
  const isOwner = useCase && currentUsername && useCase.username === currentUsername

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleClose()
      }
    }

    const handleClickOutside = (event) => {
      // Click inside the panel - do nothing
      if (panelRef.current && panelRef.current.contains(event.target)) {
        return
      }

      // Ignore clicks on modals, select menus, and use case cards
      if (
        event.target.closest('.cgt-confirm-modal') ||
        event.target.closest('.cgt-modal') ||
        event.target.closest('.Select-menu-outer') ||
        event.target.closest('.use-cases-area__select__menu') ||
        event.target.closest('.custom-searchable-select__menu') ||
        event.target.closest('.use-case-media-card__viewer')
      ) {
        return
      }

      // Check if click is on backdrop (closes both) or sidebar (closes only gallery)
      const isBackdropClick = event.target.classList.contains('overview-modal__backdrop')
      const isSidebarClick = event.target.closest('.shared-sidebar')

      if (isBackdropClick) {
        // Click on backdrop ::before pseudo-element - close both gallery and sidebar
        handleClose()
        handleCloseSidebar()
      } else if (isSidebarClick) {
        // Click propagated through backdrop to sidebar - only close gallery
        handleClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, handleClose])

  if (!isOpen || !useCase) {
    return null
  }

  return (
    <>
      <div
        className="overview-modal__backdrop"
        role="presentation"
      />
      <div
        className="overview-modal__panel"
        ref={panelRef}
      >
        <UseCaseMediaViewer
          useCase={useCase}
          initialMediaIndex={initialMediaIndex}
          canDelete={canDelete}
          canEdit={canEdit}
          canUseLikeFeature={canUseLikeFeature}
          isFetchingPrinterTypes={isFetchingPrinterTypes}
          onApprove={onApprove}
          onDelete={onDelete}
          onEditUseCase={onEditUseCase}
          onLike={onLike}
          onReject={onReject}
          onUnlike={onUnlike}
          printerTypeOptions={printerTypeOptions}
          userIsAllowedToModerate={userIsAllowedToModerate}
          isGalleryView
          isOwner={isOwner}
          onShowNotification={onShowNotification}
          isUpdating={isUpdating}
          onCloseGallery={handleClose}
        />
      </div>
    </>
  )
}

SidebarGalleryExtension.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  onCloseSidebar: func.isRequired,
  useCase: useCaseShape,
  initialMediaIndex: number,
  canDeleteUseCase: func.isRequired,
  canEditUseCase: func.isRequired,
  canUseLikeFeature: bool.isRequired,
  isFetchingPrinterTypes: bool,
  onApprove: func.isRequired,
  onDelete: func.isRequired,
  onEditUseCase: func.isRequired,
  onLike: func.isRequired,
  onReject: func.isRequired,
  onUnlike: func.isRequired,
  printerTypeOptions: arrayOf(printerTypeOptionShape),
  userIsAllowedToModerate: bool.isRequired,
  currentUsername: string,
  onShowNotification: func.isRequired,
  isUpdating: bool,
}

export default SidebarGalleryExtension
